import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import styles from './drag-scroll.module.scss';

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onScroll'> {
  children: React.ReactNode | React.ReactNode[];
  innerRef?: React.RefObject<HTMLDivElement>;
  disableDrag?: boolean;
  direction: 'horizontal' | 'vertical';
  hideScrollbar?: boolean;
  onScroll?: (scroll: { top: number; right: number; bottom: number; left: number }) => void;
}

const DragScroll = (props: Props) => {
  const {
    children,
    className,
    innerRef,
    disableDrag,
    direction,
    hideScrollbar,
    onScroll,
    ...rest
  } = props;

  const defaultRef = useRef<HTMLDivElement>(null);
  const ref = innerRef ? innerRef : defaultRef;

  const [isMouseDown, setIsMouseDown] = useState(false);

  const handleScroll = useCallback(() => {
    if (ref.current && onScroll) {
      onScroll({
        top: ref.current.scrollTop,
        right: ref.current.scrollLeft + ref.current.getBoundingClientRect().width,
        bottom: ref.current.scrollTop + ref.current.getBoundingClientRect().height,
        left: ref.current.scrollLeft
      });
    }
  }, [ref, onScroll]);

  // Scrolls the element along its own axis by `delta`, given the scroll position at gesture start.
  const applyDragDelta = useCallback(
    (currentRef: HTMLDivElement, startScroll: number, delta: number) => {
      if (direction === 'horizontal') {
        const scrollLeft = Math.max(0, startScroll - delta);
        if (scrollLeft !== currentRef.scrollLeft) {
          currentRef.scrollTo({ left: scrollLeft });
        }
      } else {
        const scrollTop = startScroll - delta;
        if (scrollTop !== currentRef.scrollTop) {
          currentRef.scrollTo({ top: scrollTop });
        }
      }
    },
    [direction]
  );

  const handleMouseDown = useCallback(
    (mouseDownEvent: MouseEvent) => {
      if (mouseDownEvent.button !== 0 || disableDrag) return;

      const currentRef = ref.current;
      if (!currentRef) return;

      // Stop the browser from starting a text selection drag.
      mouseDownEvent.preventDefault();

      const startScroll = direction === 'horizontal' ? currentRef.scrollLeft : currentRef.scrollTop;

      const mouseMoveFunc = (event: MouseEvent) => {
        const delta =
          direction === 'horizontal'
            ? event.clientX - mouseDownEvent.clientX
            : event.clientY - mouseDownEvent.clientY;

        if (Math.abs(delta) > 0) {
          setIsMouseDown(true);
        }
        applyDragDelta(currentRef, startScroll, delta);
      };

      currentRef.addEventListener('mousemove', mouseMoveFunc);

      const mouseUpFunc = () => {
        currentRef.removeEventListener('mousemove', mouseMoveFunc);
        if (!ref.current) return;
        setIsMouseDown(false);
      };

      window?.addEventListener('mouseup', mouseUpFunc, { once: true });
    },
    [disableDrag, ref, direction, applyDragDelta]
  );

  const handleTouchStart = useCallback(
    (touchStartEvent: TouchEvent) => {
      if (disableDrag) return;

      const currentRef = ref.current;
      if (!currentRef) return;

      const startTouch = touchStartEvent.touches[0];
      const startScroll = direction === 'horizontal' ? currentRef.scrollLeft : currentRef.scrollTop;

      // Undecided until the gesture moves far enough to tell which axis the user intends to scroll.
      let axisLock: 'own' | 'other' | null = null;

      const touchMoveFunc = (event: TouchEvent) => {
        const touch = event.touches[0];
        const deltaX = touch.clientX - startTouch.clientX;
        const deltaY = touch.clientY - startTouch.clientY;

        if (axisLock === null) {
          if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return;

          const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY);
          axisLock =
            (direction === 'horizontal') === isHorizontalGesture ? 'own' : 'other';

          if (axisLock === 'other') {
            // Let the browser handle the rest of this gesture (e.g. page scroll).
            currentRef.removeEventListener('touchmove', touchMoveFunc);
            return;
          }
        }

        if (axisLock === 'other') return;

        event.preventDefault();
        setIsMouseDown(true);
        applyDragDelta(currentRef, startScroll, direction === 'horizontal' ? deltaX : deltaY);
      };

      currentRef.addEventListener('touchmove', touchMoveFunc, { passive: false });

      const touchEndFunc = () => {
        currentRef.removeEventListener('touchmove', touchMoveFunc);
        if (!ref.current) return;
        setIsMouseDown(false);
      };

      window?.addEventListener('touchend', touchEndFunc, { once: true });
      window?.addEventListener('touchcancel', touchEndFunc, { once: true });
    },
    [disableDrag, ref, direction, applyDragDelta]
  );

  useEffect(() => {
    handleScroll();

    const currentRef = ref.current;

    currentRef?.addEventListener('scroll', handleScroll, { passive: true });
    currentRef?.addEventListener('mousedown', handleMouseDown, { passive: false });
    currentRef?.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      currentRef?.removeEventListener('scroll', handleScroll);
      currentRef?.removeEventListener('mousedown', handleMouseDown);
      currentRef?.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleMouseDown, handleTouchStart, ref, disableDrag, handleScroll]);


  const dragScrollElement = clsx(
    styles['drag-scroll-element'],
    direction === 'horizontal' ? styles['horizontal'] : styles['vertical'],
    !disableDrag && !isMouseDown && styles['grab'],
    isMouseDown && styles['grabbing'],
    hideScrollbar && styles['hide-scrollbar'],
    className
  );

  return (
    <div ref={ref} className={dragScrollElement} {...rest}>
      {isMouseDown && <div className={styles['curtain']} />}
      {children}
    </div>
  );
};

export default DragScroll;
